import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { LanguageService } from '../../../services/i18n/language';
import { Subscription } from 'rxjs';
import { DocNumberPipe } from '../../../services/request-number';

interface CargoTypeDto {
  id: number;
  nameRu: string;
  nameEn: string;
}

interface CurrencyDto {
  id: number;
  nameRu: string;
  nameEn: string;
  code: string;
  symbol: string;
}

@Component({
  selector: 'app-view-request-base',
  standalone: true,
  imports: [CommonModule, DocNumberPipe],
  templateUrl: './view-request-base.component.html',
  styleUrl: './view-request-base.component.scss'
})
export class ViewRequestBaseComponent implements OnInit, OnDestroy {
  @Input() requestId!: number;
  /** Отдаёт загруженную заявку наверх — например, чтобы родитель узнал hasDeal
   *  и заблокировал чат заявки после заключения сделки. */
  @Output() requestLoaded = new EventEmitter<CargoRequest>();

  request: CargoRequest | null = null;
  countries: CountryDto[] = [];
  cargoTypes: CargoTypeDto[] = [];
  currencies: CurrencyDto[] = [];
  loading = false;
  error: string | null = null;

  // Translations
  requestNumberText = 'Номер запроса';
  routeText = 'Маршрут';
  fromText = 'Из';
  toText = 'В';
  fromCountryText = 'Страна';
  fromCityText = 'Город';
  toCountryText = 'Страна';
  toCityText = 'Город';
  loadingDateText = 'Дата погрузки';
  rateTypeText = 'Тип ставки';
  priceText = 'Стоимость груза';
  currencyText = 'Валюта';
  calculateCustomsText = 'Рассчитать таможенные платежи';
  productsText = 'Продукты';
  productText = 'Продукт';
  cargoTypeText = 'Тип груза';
  tnvedCodeText = 'Код ТНВЭД';
  nameText = 'Наименование';
  quantityText = 'Количество';
  totalWeightText = 'Вес (кг)';
  dimensionsText = 'Габариты';
  lengthText = 'Длина (мм)';
  widthText = 'Ширина (мм)';
  heightText = 'Высота (мм)';
  volumeText = 'Объем';
  isDangerousText = 'Опасный груз';
  isOversizedText = 'Крупногабаритный';
  isNonStackableText = 'Не штабелируется';
  yesText = 'Да';
  noText = 'Нет';
  totalWeightTotalText = 'Общий вес';
  totalVolumeTotalText = 'Общий объем';
  createdAtText = 'Дата создания';
  updatedAtText = 'Дата обновления';
  loadingText = 'Загрузка...';
  millimeterUnitText = 'мм';

  private subscriptions = new Subscription();

  constructor(
    private cargoRequestService: CargoRequestService,
    private dictionaryService: DictionaryService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('ViewRequestBase: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (this.requestId) {
        this.loadRequest(this.requestId);
        this.loadDictionaries();
        this.updateTranslations();

        this.subscriptions.add(
          this.languageService.getTranslations().subscribe(() => {
            this.updateTranslations();
          })
        );
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.millimeterUnitText = this.languageService.translate('cargoOwner.viewRequest.millimeterUnit') || this.millimeterUnitText;
    this.requestNumberText = this.languageService.translate('cargoOwner.viewRequest.requestNumber') || 'Номер запроса';
    this.routeText = this.languageService.translate('cargoOwner.viewRequest.route') || 'Маршрут';
    this.fromText = this.languageService.translate('cargoOwner.viewRequest.from') || 'Из';
    this.toText = this.languageService.translate('cargoOwner.viewRequest.to') || 'В';
    this.fromCountryText = this.languageService.translate('cargoOwner.viewRequest.fromCountry') || 'Страна';
    this.fromCityText = this.languageService.translate('cargoOwner.viewRequest.fromCity') || 'Город';
    this.toCountryText = this.languageService.translate('cargoOwner.viewRequest.toCountry') || 'Страна';
    this.toCityText = this.languageService.translate('cargoOwner.viewRequest.toCity') || 'Город';
    this.loadingDateText = this.languageService.translate('cargoOwner.viewRequest.loadingDate') || 'Дата погрузки';
    this.rateTypeText = this.languageService.translate('cargoOwner.viewRequest.rateType') || 'Тип ставки';
    this.priceText = this.languageService.translate('cargoOwner.viewRequest.cargoCost') || 'Стоимость груза';
    this.currencyText = this.languageService.translate('cargoOwner.viewRequest.currency') || 'Валюта';
    this.calculateCustomsText = this.languageService.translate('cargoOwner.viewRequest.calculateCustoms') || 'Рассчитать таможенные платежи';
    this.productsText = this.languageService.translate('cargoOwner.viewRequest.products') || 'Продукты';
    this.productText = this.languageService.translate('cargoOwner.viewRequest.product') || 'Продукт';
    this.cargoTypeText = this.languageService.translate('cargoOwner.viewRequest.cargoType') || 'Тип груза';
    this.tnvedCodeText = this.languageService.translate('cargoOwner.viewRequest.tnvedCode') || 'Код ТНВЭД';
    this.nameText = this.languageService.translate('cargoOwner.viewRequest.productName') || 'Наименование';
    this.quantityText = this.languageService.translate('cargoOwner.createRequest.quantity') || 'Количество';
    this.totalWeightText = this.languageService.translate('cargoOwner.viewRequest.totalWeight') || 'Вес (кг)';
    this.dimensionsText = this.languageService.translate('cargoOwner.viewRequest.dimensions') || 'Габариты';
    this.lengthText = this.languageService.translate('cargoOwner.viewRequest.length') || 'Длина (мм)';
    this.widthText = this.languageService.translate('cargoOwner.viewRequest.width') || 'Ширина (мм)';
    this.heightText = this.languageService.translate('cargoOwner.viewRequest.height') || 'Высота (мм)';
    this.volumeText = this.languageService.translate('cargoOwner.viewRequest.volume') || 'Объем';
    this.isDangerousText = this.languageService.translate('cargoOwner.viewRequest.isDangerous') || 'Опасный груз';
    this.isOversizedText = this.languageService.translate('cargoOwner.viewRequest.isOversized') || 'Крупногабаритный';
    this.isNonStackableText = this.languageService.translate('cargoOwner.viewRequest.isNonStackable') || 'Не штабелируется';
    this.yesText = this.languageService.translate('cargoOwner.viewRequest.yes') || 'Да';
    this.noText = this.languageService.translate('cargoOwner.viewRequest.no') || 'Нет';
    this.totalWeightTotalText = this.languageService.translate('cargoOwner.viewRequest.totalWeightTotal') || 'Общий вес';
    this.totalVolumeTotalText = this.languageService.translate('cargoOwner.viewRequest.totalVolumeTotal') || 'Общий объем';
    this.createdAtText = this.languageService.translate('cargoOwner.viewRequest.createdAt') || 'Дата создания';
    this.updatedAtText = this.languageService.translate('cargoOwner.viewRequest.updatedAt') || 'Дата обновления';
    this.loadingText = this.languageService.translate('cargoOwner.viewRequest.loading') || 'Загрузка...';
    this.cdr.markForCheck();
  }

  private loadRequest(id: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: (request) => {
          this.request = request;
          this.loading = false;
          this.requestLoaded.emit(request);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('ViewRequestBase: Error loading request', err);
          if (err.status === 404) {
            this.error = 'Запрос не найден';
          } else if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы.';
          } else if (err.status === 401) {
            this.error = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else {
            this.error = 'Ошибка при загрузке запроса';
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  private loadDictionaries(): void {
    this.subscriptions.add(
      this.dictionaryService.getAllCountries().subscribe({
        next: (countries) => {
          this.countries = countries;
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );

    this.subscriptions.add(
      this.dictionaryService.getCargoTypes().subscribe({
        next: (types) => {
          this.cargoTypes = types;
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );

    this.subscriptions.add(
      this.dictionaryService.getCurrencies().subscribe({
        next: (currencies) => {
          this.currencies = currencies;
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );
  }

  getCountryName(countryId: number): string {
    const country = this.countries.find(c => c.id === countryId);
    if (country) {
      const lang = this.languageService.getCurrentLanguageValue();
      return lang === 'en' ? country.nameEn : country.nameRu;
    }
    return '?';
  }

  getCargoTypeName(cargoTypeId: number): string {
    const cargoType = this.cargoTypes.find(t => t.id === cargoTypeId);
    if (cargoType) {
      const lang = this.languageService.getCurrentLanguageValue();
      return lang === 'en' ? cargoType.nameEn : cargoType.nameRu;
    }
    return '?';
  }

  getCurrencyName(currencyId: number): string {
    const currency = this.currencies.find(c => c.id === currencyId);
    if (currency) {
      const lang = this.languageService.getCurrentLanguageValue();
      const name = lang === 'en' ? currency.nameEn : currency.nameRu;
      return `${name} (${currency.code})`;
    }
    return '?';
  }

  getCurrencySymbol(currencyId: number): string {
    const currency = this.currencies.find(c => c.id === currencyId);
    return currency?.symbol || '';
  }

  getRateTypeName(rateType: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER'): string {
    const lang = this.languageService.getCurrentLanguageValue();
    if (lang === 'en') {
      if (rateType === 'FTL') return 'FTL (Full Truck Load)';
      if (rateType === 'LTL') return 'LTL (Less Than Truck Load)';
      if (rateType === 'AIR') return 'Air';
      if (rateType === 'CONTAINER') return 'Container';
      return rateType || '';
    }
    if (rateType === 'FTL') return 'FTL (Выделенный АТС)';
    if (rateType === 'LTL') return 'LTL (Сборный груз)';
    if (rateType === 'AIR') return 'Авиа';
    if (rateType === 'CONTAINER') return 'Контейнер';
    return rateType || '';
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatWeight(weight: number): string {
    if (weight >= 1000) {
      return `${(weight / 1000).toFixed(2)} т`;
    }
    return `${weight.toFixed(2)} кг`;
  }

  formatVolume(length?: number, width?: number, height?: number, volumeM3?: number, quantity?: number): string {
    // Если передан volumeM3, используем его (это уже итоговый объём по всем местам)
    if (volumeM3 !== undefined && volumeM3 !== null) {
      return this.formatVolumeValue(volumeM3);
    }

    // Иначе вычисляем из габаритов одного места × количество мест
    if (length && width && height) {
      const volume = (length * width * height) / 1_000_000_000 * (quantity || 1);
      return this.formatVolumeValue(volume);
    }

    // Если ничего не заполнено, возвращаем "-"
    return '-';
  }

  private formatVolumeValue(volume: number): string {
    // Всегда показываем в м³
    const decimals = volume >= 1 ? 2 : volume >= 0.01 ? 4 : volume >= 0.0001 ? 6 : 8;
    return `${volume.toFixed(decimals)} м³`;
  }

  getTotalWeight(): number {
    if (!this.request) return 0;
    return this.request.products.reduce((sum, product) => sum + product.totalWeightKg, 0);
  }

  getTotalVolume(): number {
    if (!this.request) return 0;
    return this.request.products.reduce((sum, product) => {
      // Если есть volumeM3, используем его
      if (product.volumeM3) {
        return sum + product.volumeM3;
      }
      // Иначе вычисляем из габаритов одного места × количество мест
      if (product.lengthMm && product.widthMm && product.heightMm) {
        const volume = (product.lengthMm * product.widthMm * product.heightMm) / 1_000_000_000 * (product.quantity || 1);
        return sum + volume;
      }
      // Если ни габариты, ни объем не заполнены, возвращаем 0
      return sum;
    }, 0);
  }

  formatTotalVolume(volume: number): string {
    // Всегда показываем в м³
    const decimals = volume >= 1 ? 2 : volume >= 0.01 ? 4 : volume >= 0.0001 ? 6 : 8;
    return `${volume.toFixed(decimals)} м³`;
  }
}
